/*
 * 🔒 OLIVINE SIGNUP GIFT — the storefront half of the signup-gated gift.
 *
 * LAW 2 (Offer System — Spec): assets/cart.js is the ONLY thing that adds,
 * removes or resizes a gift line. This file never adds a GIFT line. Its one
 * /cart/add.js call is the add-to-cart link (`addHeroFromLink`), which adds the
 * page's HERO only, with no properties. Otherwise it makes at most three
 * network calls:
 *
 *   GET  /cart.js                      read the attributes (_signup_gift, _ab)
 *   POST https://a.klaviyo.com/client/subscriptions?company_id=…   INLINE ONLY
 *   POST /cart/update.js               {attributes:{_signup_gift: <ref>}}
 *
 * …and then asks cart.js for a reconcile pass through its own public shim.
 * The reconciler decides presence, the Discount Function decides price.
 *
 * TWO MODES (`signup_mode` on the section).
 *
 *   POPUP (default, Maycol 2026-09-15) — Klaviyo's own popup form IS the
 *   signup. The slot renders a button that opens it, and the module reacts to
 *   the form's `submit` event. It makes NO Klaviyo call: only /cart.js and
 *   /cart/update.js are ever requested in this mode.
 *
 *   INLINE — the older first name + email form inside the drawer, unchanged.
 *
 * Popup mode with a BLANK form id falls back to INLINE and says so in the
 * console, because a button that opens nothing is worse than the old form.
 *
 * SIGNUP NEVER WAITS FOR THE CART (2026-09-15 law). The form submits and holds
 * the entitlement whatever is in the cart; the bar explains what is still
 * needed. No PII in analytics, URLs or storage — sessionStorage holds the
 * opaque ref and the state, nothing else.
 */
(() => {
  'use strict';

  // ═════ SIGNUP CORE (machine + transports) — BEGIN ═══════════════════════════
  // Everything between these two sentinels is DOM-free. tests/module.test.mjs
  // slices this exact region out of the shipped file and runs it against a
  // mocked fetch, so the test can never drift from what the browser executes.

  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  // Confirmed 2026-09-15 against developers.klaviyo.com/en/reference/create_client_subscription:
  // the `revision` header is REQUIRED and its documented default is 2026-07-15.
  const KLAVIYO_REVISION = '2026-07-15';
  const KLAVIYO_CUSTOM_SOURCE = 'Olivine gift signup';
  const ENTITLEMENT_REF_PATTERN = /^sg_[0-9a-f]{16}_[0-9a-z]+$/;
  // One key, no email in it and no email under it.
  const STORAGE_KEY = 'olv-signup-gift';

  const SIGNUP_STATES = Object.freeze({
    IDLE: 'idle',
    VALIDATION_ERRORS: 'validation_errors',
    SUBMITTING: 'submitting',
    SUCCESS: 'success',
    FAILED_IDENTITY: 'failed_identity',
    GIFT_FAILED: 'identity_ok_gift_failed',
    GIFT_UNAVAILABLE: 'gift_unavailable'
  });

  function normalizeEmail(value) {
    return String(value || '').trim().toLowerCase();
  }

  function validateIdentity(identity) {
    const errors = {};
    const source = identity || {};
    if (!String(source.first_name || '').trim()) errors.first_name = 'Enter your first name.';
    const email = normalizeEmail(source.email);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address.';
    return errors;
  }

  /**
   * The opaque entitlement reference. `sg_` + 16 hex + `_` + base36 timestamp.
   * NEVER "1", never an email, never derived from one — there is no server to
   * verify it, so it carries no meaning a shopper could forge into something
   * better than "she submitted the form in this cart".
   */
  function makeEntitlementRef(now) {
    const bytes = new Uint8Array(8);
    const source = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
    if (source && typeof source.getRandomValues === 'function') source.getRandomValues(bytes);
    else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
    let hex = '';
    for (let index = 0; index < bytes.length; index += 1) hex += bytes[index].toString(16).padStart(2, '0');
    return 'sg_' + hex + '_' + Number(now || Date.now()).toString(36);
  }

  /** Stable across retries for one email on one surface. Never stored raw. */
  function makeSubmissionId(source, email) {
    const text = String(source || 'cart_drawer') + '|' + normalizeEmail(email);
    let first = 0x811c9dc5;
    let second = 0x9e3779b9;
    for (let index = 0; index < text.length; index += 1) {
      const code = text.charCodeAt(index);
      first = Math.imul(first ^ code, 0x01000193);
      second = Math.imul(second ^ (code + index), 0x85ebca6b);
    }
    return 'sub_' + (first >>> 0).toString(16).padStart(8, '0') + (second >>> 0).toString(16).padStart(8, '0');
  }

  function klaviyoBody(envelope, config) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: envelope.identity.email,
                first_name: envelope.identity.first_name,
                properties: {
                  signup_source: 'gift_signup',
                  gift_entitlement_ref: envelope.entitlement_ref,
                  hero_product_id: String(config.heroProductId || '')
                }
              }
            }
          },
          custom_source: KLAVIYO_CUSTOM_SOURCE
        },
        relationships: {
          list: { data: { type: 'list', id: String(config.listId || '') } }
        }
      }
    };
  }

  /**
   * The identity adapter IS the consent action: the client subscriptions
   * endpoint creates the profile and the email marketing consent record in one
   * call (with no `subscriptions` object the channel defaults to MARKETING).
   * With no list id configured it refuses BEFORE any network call, so a
   * half-configured section can never quietly write a cart attribute.
   */
  function createKlaviyoIdentity(config) {
    return {
      async sync(envelope) {
        const companyId = String((config && config.companyId) || '').trim();
        const listId = String((config && config.listId) || '').trim();
        if (!companyId || !listId) return { ok: false, reason: 'invalid' };
        let response;
        try {
          response = await fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              accept: 'application/json',
              revision: KLAVIYO_REVISION
            },
            body: JSON.stringify(klaviyoBody(envelope, config))
          });
        } catch (error) {
          return { ok: false, reason: 'network' };
        }
        const status = Number(response && response.status) || 0;
        if (status === 202 || status === 200 || status === 201) return { ok: true, profile_state: 'ok' };
        return { ok: false, reason: status >= 500 ? 'server' : 'invalid' };
      }
    };
  }

  /** The ONE cart write. Attribute only — never a line, never a price. */
  function createCartTransport(routes) {
    const updateUrl = (routes && routes.cart_update_url ? routes.cart_update_url : '/cart/update') + '.js';
    const readUrl = (routes && routes.cart_url ? routes.cart_url : '/cart') + '.js';
    return {
      updateUrl: updateUrl,
      async setAttribute(name, value) {
        const attributes = {};
        attributes[name] = value;
        const response = await fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        });
        if (!response || response.ok === false) throw new Error('cart attribute write refused');
        return true;
      },
      async read() {
        const response = await fetch(readUrl, { headers: { Accept: 'application/json' } });
        if (!response || response.ok === false) throw new Error('cart read refused');
        return response.json();
      }
    };
  }

  function publicSnapshot(machine) {
    return {
      state: machine.state,
      submission_id: machine.submission_id,
      entitlement_ref: machine.entitlement_ref,
      identity_state: machine.identity_state,
      entitlement_state: machine.entitlement_state,
      errors: Object.assign({}, machine.errors),
      first_name: machine.first_name,
      restored: machine.restored,
      offer: Object.assign({}, machine.offer)
    };
  }

  function createSignupMachine(options) {
    const settings = options || {};
    const machine = {
      adapters: settings.adapters,
      offer: Object.assign({ gift_available: true }, settings.offer),
      source: settings.source || 'cart_drawer',
      storage: settings.storage,
      now: settings.now || (() => Date.now()),
      state: SIGNUP_STATES.IDLE,
      submission_id: null,
      entitlement_ref: null,
      identity_state: 'idle',
      entitlement_state: 'idle',
      errors: {},
      first_name: '',
      lastEnvelope: null,
      restored: false,
      inFlight: false
    };
    const onState = typeof settings.onState === 'function' ? settings.onState : () => {};

    function emit() {
      const snapshot = publicSnapshot(machine);
      try { onState(snapshot); } catch (error) { /* host callbacks do not own state */ }
      return snapshot;
    }

    function persist() {
      if (!machine.storage) return;
      try {
        machine.storage.setItem(STORAGE_KEY, JSON.stringify({
          state: machine.state,
          entitlement_ref: machine.entitlement_ref,
          submission_id: machine.submission_id
        }));
      } catch (error) { /* storage is optional */ }
    }

    function restore() {
      if (!machine.storage) return emit();
      try {
        const saved = JSON.parse(machine.storage.getItem(STORAGE_KEY) || 'null');
        if (saved && saved.state === SIGNUP_STATES.SUCCESS && ENTITLEMENT_REF_PATTERN.test(String(saved.entitlement_ref || ''))) {
          machine.state = SIGNUP_STATES.SUCCESS;
          machine.entitlement_ref = saved.entitlement_ref;
          machine.submission_id = saved.submission_id || null;
          machine.identity_state = 'ok';
          machine.entitlement_state = 'held';
          machine.restored = true;
        }
      } catch (error) { /* malformed storage is ignored */ }
      return emit();
    }

    function envelopeFor(identity) {
      const normalized = {
        first_name: String(identity.first_name || '').trim(),
        email: normalizeEmail(identity.email)
      };
      return {
        submission_id: makeSubmissionId(machine.source, normalized.email),
        identity: normalized,
        entitlement_ref: makeEntitlementRef(machine.now()),
        source: machine.source
      };
    }

    async function hold(envelope) {
      machine.entitlement_state = 'submitting';
      const ref = envelope.entitlement_ref;
      if (!ENTITLEMENT_REF_PATTERN.test(String(ref)) || String(ref).indexOf('@') !== -1) {
        machine.entitlement_state = 'server';
        machine.state = SIGNUP_STATES.GIFT_FAILED;
        machine.inFlight = false;
        persist();
        return emit();
      }
      try {
        await machine.adapters.cart.setAttribute('_signup_gift', ref);
      } catch (error) {
        machine.entitlement_state = 'network';
        machine.state = SIGNUP_STATES.GIFT_FAILED;
        machine.inFlight = false;
        persist();
        return emit();
      }
      machine.entitlement_ref = ref;
      machine.entitlement_state = 'held';
      machine.state = SIGNUP_STATES.SUCCESS;
      machine.inFlight = false;
      persist();
      return emit();
    }

    async function submit(identity) {
      if (machine.inFlight) return publicSnapshot(machine);
      const errors = validateIdentity(identity);
      if (Object.keys(errors).length) {
        machine.errors = errors;
        machine.state = SIGNUP_STATES.VALIDATION_ERRORS;
        return emit();
      }
      const envelope = envelopeFor(identity);
      // Already held for this exact person on this surface — do nothing twice.
      if (machine.state === SIGNUP_STATES.SUCCESS && machine.submission_id === envelope.submission_id) {
        return publicSnapshot(machine);
      }
      if (machine.submission_id && machine.submission_id !== envelope.submission_id) {
        machine.identity_state = 'idle';
        machine.entitlement_state = 'idle';
        machine.entitlement_ref = null;
      }
      machine.first_name = envelope.identity.first_name;
      machine.submission_id = envelope.submission_id;
      machine.lastEnvelope = envelope;
      machine.errors = {};
      machine.state = SIGNUP_STATES.SUBMITTING;
      machine.inFlight = true;
      emit();

      const identityAck = await machine.adapters.identity.sync(envelope);
      if (!identityAck || !identityAck.ok) {
        machine.identity_state = (identityAck && identityAck.reason) || 'server';
        machine.state = SIGNUP_STATES.FAILED_IDENTITY;
        machine.inFlight = false;
        persist();
        return emit();
      }
      machine.identity_state = 'ok';

      // The gift being sold out never costs her the signup — identity is done.
      if (!machine.offer.gift_available) {
        machine.entitlement_state = 'gift_unavailable';
        machine.state = SIGNUP_STATES.GIFT_UNAVAILABLE;
        machine.inFlight = false;
        persist();
        return emit();
      }
      return hold(envelope);
    }

    /**
     * POPUP MODE. Klaviyo's own popup form already created the profile and the
     * consent record, so its `submit` event IS the identity acknowledgement and
     * there is nothing for us to sync. Everything after that point is the same
     * chain as submit(): mint the ref, write the ONE cart attribute, let the
     * reconciler decide the line. No Klaviyo call is made from here, ever.
     *
     * Idempotent by state: a second `submit` for the same form — a double click
     * through the popup, or a second listener — finds SUCCESS and does nothing.
     */
    async function claimFromPopup(identity) {
      if (machine.inFlight) return publicSnapshot(machine);
      if (machine.state === SIGNUP_STATES.SUCCESS || machine.state === SIGNUP_STATES.GIFT_UNAVAILABLE) {
        return publicSnapshot(machine);
      }
      const source = identity || {};
      const envelope = {
        submission_id: makeSubmissionId(machine.source, source.email),
        identity: { first_name: String(source.first_name || '').trim(), email: normalizeEmail(source.email) },
        entitlement_ref: makeEntitlementRef(machine.now()),
        source: machine.source
      };
      machine.first_name = envelope.identity.first_name;
      machine.submission_id = envelope.submission_id;
      machine.lastEnvelope = envelope;
      machine.errors = {};
      machine.identity_state = 'ok';
      machine.state = SIGNUP_STATES.SUBMITTING;
      machine.inFlight = true;
      emit();

      // The gift being sold out never costs her the signup — the popup is done.
      if (!machine.offer.gift_available) {
        machine.entitlement_state = 'gift_unavailable';
        machine.state = SIGNUP_STATES.GIFT_UNAVAILABLE;
        machine.inFlight = false;
        persist();
        return emit();
      }
      return hold(envelope);
    }

    async function retry() {
      if (machine.inFlight || !machine.lastEnvelope) return publicSnapshot(machine);
      if (machine.state === SIGNUP_STATES.GIFT_FAILED) {
        machine.state = SIGNUP_STATES.SUBMITTING;
        machine.inFlight = true;
        emit();
        return hold(machine.lastEnvelope);
      }
      if (machine.state === SIGNUP_STATES.FAILED_IDENTITY) return submit(machine.lastEnvelope.identity);
      return publicSnapshot(machine);
    }

    function clearError(field) {
      if (!Object.prototype.hasOwnProperty.call(machine.errors, field)) return publicSnapshot(machine);
      delete machine.errors[field];
      if (machine.state === SIGNUP_STATES.VALIDATION_ERRORS && Object.keys(machine.errors).length === 0) {
        machine.state = SIGNUP_STATES.IDLE;
      }
      return emit();
    }

    function setOffer(next) {
      machine.offer = Object.assign({}, machine.offer, next);
      return emit();
    }

    /**
     * THE CART IS THE TRUTH. A success restored from sessionStorage beside a
     * cart that no longer carries `_signup_gift` is stale — she emptied the
     * cart, or the write never stuck. Forget it and ask again, rather than tell
     * her she is in when the reconciler will never add the gift.
     */
    function forget() {
      machine.state = SIGNUP_STATES.IDLE;
      machine.submission_id = null;
      machine.entitlement_ref = null;
      machine.identity_state = 'idle';
      machine.entitlement_state = 'idle';
      machine.lastEnvelope = null;
      machine.restored = false;
      machine.errors = {};
      try { if (machine.storage) machine.storage.removeItem(STORAGE_KEY); } catch (error) { /* storage is optional */ }
      return emit();
    }

    return {
      submit: submit,
      claimFromPopup: claimFromPopup,
      retry: retry,
      restore: restore,
      forget: forget,
      clearError: clearError,
      setOffer: setOffer,
      getState: () => publicSnapshot(machine)
    };
  }

  /** `all` gates every cart; anything else gates only its own `_ab` arm. */
  function gateApplies(gate, ab) {
    const value = String(gate || '').trim();
    if (!value) return false;
    if (value === 'all') return true;
    return value === String(ab || '').trim();
  }

  /**
   * EMAIL UNLOCK LINK (2026-10-04). The post-signup email links to the product
   * page with `?olv_gift=<anything>`, so a signup on one browser can be claimed
   * on another. Any non-blank value claims; the value itself is never read
   * beyond "is it blank", never stored, never written to the cart. It is not
   * verified — the same accepted cost as the unverifiable `_signup_gift`.
   */
  const GIFT_LINK_PARAM = 'olv_gift';

  /** The decoded name of one raw `name=value` query pair. */
  function queryPairName(pair) {
    const raw = String(pair || '');
    const index = raw.indexOf('=');
    return decodeQueryPart(index === -1 ? raw : raw.slice(0, index));
  }

  function decodeQueryPart(part) {
    const raw = String(part || '').replace(/\+/g, ' ');
    try { return decodeURIComponent(raw); } catch (error) { return raw; }
  }

  /** True when the query string carries a NON-BLANK `olv_gift`. Pure. */
  function hasGiftLinkParam(search) {
    const query = String(search || '').replace(/^\?/, '');
    if (!query) return false;
    return query.split('&').some((pair) => {
      if (queryPairName(pair) !== GIFT_LINK_PARAM) return false;
      const index = pair.indexOf('=');
      return index !== -1 && decodeQueryPart(pair.slice(index + 1)).trim() !== '';
    });
  }

  /**
   * ADD-TO-CART LINK (2026-10-05). `?olv_add=<1-4>` tops the cart UP to that many
   * units of THIS page's hero and opens the drawer, so an email or SMS button
   * lands her on a ready cart. Pairs with `olv_gift` (claim first, then add).
   * Never a /cart/<variant>:N permalink: that server-redirects past Phoenix.
   */
  const ADD_LINK_PARAM = 'olv_add';
  const ADD_LINK_MAX = 4;

  /** The value of the FIRST `name` pair, '' when absent. Pure. */
  function queryParam(search, name) {
    const query = String(search || '').replace(/^\?/, '');
    if (!query) return '';
    for (const pair of query.split('&')) {
      if (queryPairName(pair) !== name) continue;
      const index = pair.indexOf('=');
      return index === -1 ? '' : decodeQueryPart(pair.slice(index + 1)).trim();
    }
    return '';
  }

  /** Units requested by `olv_add`: an integer 1..4, else 0. Pure. */
  function addLinkQuantity(search) {
    const raw = queryParam(search, ADD_LINK_PARAM);
    if (!/^[0-9]{1,2}$/.test(raw)) return 0;
    const n = Number(raw);
    return n >= 1 && n <= ADD_LINK_MAX ? n : 0;
  }

  /**
   * Which hero variant the link adds. With `?variant=`: that variant only if it
   * is an AVAILABLE variant of this product, else null (add nothing; the PDP
   * preselects the shade natively, so she picks it herself — never a silent
   * swap to another shade). Without it: a single-variant product adds its one
   * variant if available; a product with shades adds NOTHING (Maycol 2026-10-07:
   * a link that doesn't say which shade must not guess one). Pure.
   */
  function pickLinkVariant(product, variantParam) {
    const variants = (product && Array.isArray(product.variants)) ? product.variants : [];
    const wanted = String(variantParam || '').trim();
    if (wanted) return variants.find((v) => String(v.id) === wanted && v.available) || null;
    if (variants.length !== 1) return null;
    return variants[0].available ? variants[0] : null;
  }

  /** Units still to add so the hero totals `quantity`. Never negative. Pure. */
  function topUpQuantity(cartPayload, productId, quantity) {
    const items = (cartPayload && Array.isArray(cartPayload.items)) ? cartPayload.items : [];
    const have = items
      .filter((item) => String(item.product_id) === String(productId) && !(item.properties && (item.properties._gift || item.properties._bogo_gift || item.properties._threshold_gift)))
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    return Math.max(0, quantity - have);
  }

  // ═════ SIGNUP CORE — END ════════════════════════════════════════════════════

  const COPY = Object.freeze({
    heading: 'Claim your free {gift}',
    body: 'Add your first name and email and we will hold it for this cart.',
    firstName: 'First name',
    email: 'Email',
    consent: 'You are signing up for Olivine emails. Unsubscribe any time.',
    button: 'Claim my free gift',
    submitting: 'Saving your details…',
    success: 'You are in, {first_name}. Your free {gift} is on its way to the cart.',
    // Storage holds no PII, so a reload has no first name to greet her by. Two
    // strings rather than "You are in, . Your free…" — the prototype's known nit.
    successNoName: 'You are in. Your free {gift} is on its way to the cart.',
    soldOutSuccessNoName: 'Saved. We will email you when it is back.',
    identityFailed: 'We could not save your details. Check your connection and try again.',
    identityRetry: 'Try again',
    giftFailed: 'Your details are saved. We could not attach your gift just now. Try again and it will not create a duplicate.',
    giftRetry: 'Try again for the gift',
    soldOutHeading: 'Save your spot',
    soldOutBody: 'This gift is out of stock right now. Add your first name and email and we will email you the moment it is back.',
    soldOutButton: 'Save my spot',
    soldOutSuccess: 'Saved, {first_name}. We will email you when it is back.',
    // POPUP MODE. The bar's own sentence already reads "One step left to claim
    // your free {gift}." so the slot never repeats it; `popupLead` is only used
    // on a surface that has no bar sentence above it (the product page).
    popupLead: 'One step left to claim your free {gift}.',
    popupButton: 'Claim my free {gift}'
  });

  // ═════ KLAVIYO ONSITE FORMS — the documented contract ═══════════════════════
  // developers.klaviyo.com/en/docs/how_to_custom_trigger_a_popup_or_flyout_form
  //   "window._klOnsite = window._klOnsite || [];
  //    window._klOnsite.push(['openForm', 'FormID']);"
  //   `.push` is the resilient half of the pair: the docs say it "can execute
  //   before Klaviyo.js loads", while `_klOnsite.openForm(...)` "will only
  //   execute if Klaviyo.js has loaded on your site. Otherwise, it will fail."
  //   A deferred theme asset can easily beat the async onsite script, so push.
  //
  // developers.klaviyo.com/en/docs/track_klaviyo_form_activity_using_javascript
  //   "Klaviyo forms send an event called "klaviyoForms" to the window each
  //    time activity occurs on a form", with `detail.type` one of
  //    open | embedOpen | close | redirectedToUrl | submit | stepSubmit.
  //
  //   WHY `submit` AND NOT `stepSubmit`: the docs pin submit to the list
  //   subscription itself — "fires when a visitor submits the main conversion
  //   action of a form (e.g., the first email or SMS subscription action), and
  //   will only fire once per form" — while "stepSubmit … can fire multiple
  //   times per form". The entitlement is minted once, so it hangs off the
  //   once-per-form event. (The state machine refuses a second claim anyway.)
  //
  //   FIELD NAME: every code sample on that page, and Klaviyo's own live
  //   listener on developers.klaviyo.com, read `e.detail.formId`. The prose
  //   list below them spells the same field `formID`. Both are read here
  //   because the docs disagree with themselves and the cost is two tokens.
  const KLAVIYO_FORM_EVENT = 'klaviyoForms';
  const KLAVIYO_SUBMIT_TYPE = 'submit';
  // `metaData` holds "all of the fields submitted to Klaviyo", keyed by the
  // form's own field mapping, so the first name key is whatever the form calls
  // it. Nothing here is stored — it only greets her in the success sentence.
  const KLAVIYO_NAME_KEYS = ['first_name', '$first_name', 'First Name', 'firstName', 'FirstName'];

  const host = document.getElementById('olivine-signup-gift-config');
  if (!host || host.dataset.signupGiftGuard !== 'olivine-140766347351') return;

  let config;
  try {
    config = JSON.parse(host.querySelector('script[type="application/json"]')?.textContent || '');
  } catch (error) {
    return;
  }
  if (!config) return;

  const EXPECTED_THEME_ID = '140766347351';

  function activeContext() {
    const browserTheme = window.Shopify && window.Shopify.theme;
    const browserThemeId = String((browserTheme && browserTheme.id) || config.themeId || '');
    const browserThemeRole = String((browserTheme && browserTheme.role) || config.themeRole || '').toLowerCase();
    return browserThemeId === EXPECTED_THEME_ID &&
      browserThemeRole === 'main' &&
      String(config.themeId) === EXPECTED_THEME_ID &&
      String(config.themeRole).toLowerCase() === 'main';
  }
  if (!activeContext()) return;

  // ── The mode, decided ONCE ────────────────────────────────────────────────
  // A blank form id in popup mode is a misconfiguration, not a reason to ship a
  // button that opens nothing: fall back to the inline form and say so.
  // WHICH popup app (2026-10-02). `klaviyo` (default) = form id; `alia` = campaign id.
  // Alia SDK, read 2026-10-02 from intercom.help/alia-help-center 11071697 + 15434880:
  //   open:   window.alia.push({type: "open", campaignID: <number>, ignoreTargeting: true})
  //           "Only live campaigns can be opened using the SDK."
  //   submit: document event "alia:signup", detail {type, popupID, email, phone, campaignID, campaignTitle}
  // `studio` (P5, 2026-10-17) = Olivine Studio popup id. Studio runtime (olivine-studio
  // packages/popup-engine/src/runtime, live extension commit 1236e3c), read 2026-10-17:
  //   open:   boot.ts sets `host.olvPopup = mountPopup(...)` on each [data-olv-popup] host;
  //           its `open()` (runtime.ts) skips triggers, targeting and frequency history.
  //           The host's popup id is data-olv-popup-config `campaign.controlPopupId`.
  //   submit: document event "olv:signup", detail {popupId, step: "email" | "sms"}, no contact data.
  const PROVIDER_NAME = String(config.popupProvider || 'klaviyo').trim().toLowerCase();
  const POPUP_PROVIDER = PROVIDER_NAME === 'alia' || PROVIDER_NAME === 'studio' ? PROVIDER_NAME : 'klaviyo';
  const POPUP_ID_SETTING = { klaviyo: 'klaviyo_form_id', alia: 'alia_campaign_id', studio: 'studio_popup_id' }[POPUP_PROVIDER];
  const POPUP_FORM_ID = String((POPUP_PROVIDER === 'alia' ? config.aliaCampaignId : POPUP_PROVIDER === 'studio' ? config.studioPopupId : config.formId) || '').trim();
  const REQUESTED_MODE = String(config.signupMode || 'popup').trim().toLowerCase() === 'inline' ? 'inline' : 'popup';
  const MODE = REQUESTED_MODE === 'popup' && !POPUP_FORM_ID ? 'inline' : REQUESTED_MODE;
  if (REQUESTED_MODE === 'popup' && MODE === 'inline') {
    console.warn('[olivine-signup-gift] signup_mode is "popup" but klaviyo_form_id is blank — falling back to the inline form.'.replace('klaviyo_form_id', POPUP_ID_SETTING));
  }

  const cart = createCartTransport(window.routes);
  const identity = createKlaviyoIdentity(config);
  let storage = null;
  try { storage = window.sessionStorage; } catch (error) { storage = null; }

  const machine = createSignupMachine({
    adapters: { identity: identity, cart: cart },
    offer: { gift_available: config.giftAvailable !== false },
    source: 'cart_drawer',
    storage: storage,
    onState: (snapshot) => render(snapshot)
  });

  let cartAttributes = null;
  let cartRead = null;
  let mounted = null;
  let scheduled = false;

  function text(value, words) {
    return String(value || '').replace(/\{gift\}/g, words.gift).replace(/\{first_name\}/g, words.firstName);
  }

  function cookieValue(name) {
    const match = String(document.cookie || '').match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function abArm() {
    const fromCart = cartAttributes && cartAttributes._ab;
    return String(fromCart || cookieValue('olv_ab') || '').trim();
  }

  function signedUp() {
    return String((cartAttributes && cartAttributes._signup_gift) || '').trim() !== '';
  }

  async function readCart(force) {
    if (cartAttributes && !force) return cartAttributes;
    if (cartRead && !force) return cartRead;
    cartRead = cart.read()
      .then((payload) => {
        cartAttributes = (payload && payload.attributes) || {};
        return cartAttributes;
      })
      .catch(() => {
        cartAttributes = cartAttributes || {};
        return cartAttributes;
      })
      .finally(() => { cartRead = null; });
    return cartRead;
  }

  function element(tag, className, content) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  }

  /** ONE builder for both surfaces — the drawer slot and the page slot. */
  function buildForm(slot, words) {
    const soldOut = config.giftAvailable === false;
    const form = element('form', 'olv-signup-gift__form');
    form.noValidate = true;
    const suffix = String(slot.dataset.surface || 'cart_drawer');

    const heading = element('p', 'olv-signup-gift__heading', text(config.heading || (soldOut ? COPY.soldOutHeading : COPY.heading), words));
    heading.id = 'olv-signup-gift-heading-' + suffix;
    form.setAttribute('aria-labelledby', heading.id);
    const body = element('p', 'olv-signup-gift__body', text(config.body || (soldOut ? COPY.soldOutBody : COPY.body), words));

    const fields = element('div', 'olv-signup-gift__fields');

    const firstField = element('div', 'olv-signup-gift__field');
    const firstLabel = element('label', 'olv-signup-gift__label', COPY.firstName);
    firstLabel.htmlFor = 'olv-signup-gift-first-' + suffix;
    const firstInput = element('input', 'olv-signup-gift__input');
    firstInput.id = firstLabel.htmlFor;
    firstInput.name = 'first_name';
    firstInput.type = 'text';
    firstInput.autocomplete = 'given-name';
    firstInput.placeholder = COPY.firstName;
    firstField.append(firstLabel, firstInput);

    const emailField = element('div', 'olv-signup-gift__field');
    const emailLabel = element('label', 'olv-signup-gift__label', COPY.email);
    emailLabel.htmlFor = 'olv-signup-gift-email-' + suffix;
    const emailInput = element('input', 'olv-signup-gift__input');
    emailInput.id = emailLabel.htmlFor;
    emailInput.name = 'email';
    emailInput.type = 'email';
    emailInput.autocomplete = 'email';
    emailInput.inputMode = 'email';
    emailInput.placeholder = COPY.email;
    emailField.append(emailLabel, emailInput);

    const button = element('button', 'olv-signup-gift__submit', text(config.buttonLabel || (soldOut ? COPY.soldOutButton : COPY.button), words));
    button.type = 'submit';

    fields.append(firstField, emailField, button);
    const consent = element('p', 'olv-signup-gift__consent', COPY.consent);
    const message = element('p', 'olv-signup-gift__message');
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.hidden = true;

    form.append(heading, body, fields, consent, message);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const snapshot = machine.getState();
      if (snapshot.state === 'failed_identity' || snapshot.state === 'identity_ok_gift_failed') {
        machine.retry().then(afterSubmit);
        return;
      }
      machine.submit({ first_name: firstInput.value, email: emailInput.value }).then(afterSubmit);
    });
    firstInput.addEventListener('input', () => machine.clearError('first_name'));
    emailInput.addEventListener('input', () => machine.clearError('email'));

    return { form: form, heading: heading, body: body, fields: fields, consent: consent, message: message, button: button, firstInput: firstInput, emailInput: emailInput, words: words, soldOut: soldOut };
  }

  function popupButtonLabel(words, soldOut) {
    return text(config.buttonLabel || (soldOut ? COPY.soldOutButton : COPY.popupButton), words);
  }

  /** The documented trigger. `.push` survives Klaviyo.js not having loaded. */
  function openPopup() {
    if (!POPUP_FORM_ID) return;
    if (POPUP_PROVIDER === 'alia') {
      // ignoreTargeting: she clicked the button, so the campaign's own targeting must not hide it.
      window.alia = window.alia || [];
      window.alia.push({ type: 'open', campaignID: Number(POPUP_FORM_ID), ignoreTargeting: true });
      return;
    }
    if (POPUP_PROVIDER === 'studio') { openStudioPopup(); return; }
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', POPUP_FORM_ID]);
  }

  /**
   * Studio: the mounted host whose control popup is POPUP_FORM_ID. No host, a host
   * whose runtime never ran (embed off, script blocked), or a bad config = a quiet
   * no-op, as Alia is with no SDK: nothing opens, nothing is written, nothing throws.
   */
  function openStudioPopup() {
    try {
      const hosts = document.querySelectorAll('[data-olv-popup]');
      for (let i = 0; i < hosts.length; i += 1) {
        const controller = hosts[i].olvPopup;
        if (!controller || typeof controller.open !== 'function') continue;
        let doc = null;
        try { doc = JSON.parse(hosts[i].getAttribute('data-olv-popup-config') || 'null'); } catch (error) { doc = null; }
        const id = doc && doc.campaign ? String(doc.campaign.controlPopupId == null ? '' : doc.campaign.controlPopupId).trim() : '';
        if (id !== POPUP_FORM_ID) continue;
        if (controller.open() === false) console.warn('[olivine-signup-gift] Studio popup "' + POPUP_FORM_ID + '" did not open: another Studio popup is already open.');
        return;
      }
      console.warn('[olivine-signup-gift] Studio popup "' + POPUP_FORM_ID + '" is not on this page: check the Olivine Studio app embed is on and the popup is live.');
    } catch (error) {
      // A Studio fault never breaks the drawer.
    }
  }

  /**
   * POPUP MODE slot: one line of copy and one button.
   *
   * The line of copy is the BAR'S OWN sentence, which the snippet has already
   * rendered immediately above this slot ("One step left to claim your free
   * {gift}."). Printing it again here would show the same sentence twice in the
   * drawer, so the slot carries its own lead line ONLY on a surface with no bar
   * sentence above it — the product page. In the drawer the composed bar is
   * still exactly one line of copy and one button.
   */
  function buildPrompt(slot, words) {
    const soldOut = config.giftAvailable === false;
    const root = element('div', 'olv-signup-gift__form olv-signup-gift__prompt');
    const suffix = String(slot.dataset.surface || 'cart_drawer');

    // Not hidden — NOT BUILT. A hidden copy would still be read out by a screen
    // reader and would still be found by anything reading the bar's text.
    let lead = null;
    if (!barSentence(slot)) {
      lead = element('p', 'olv-signup-gift__heading olv-signup-gift__lead', text(COPY.popupLead, words));
      lead.id = 'olv-signup-gift-lead-' + suffix;
    }

    const button = element('button', 'olv-signup-gift__submit olv-signup-gift__open', popupButtonLabel(words, soldOut));
    button.type = 'button';

    const message = element('p', 'olv-signup-gift__message');
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.hidden = true;

    if (lead) root.append(lead);
    root.append(button, message);
    button.addEventListener('click', () => {
      // The gift write can fail on its own after a perfectly good signup. That
      // retry never re-opens the popup — she is already subscribed.
      if (machine.getState().state === 'identity_ok_gift_failed') {
        machine.retry().then(afterSubmit);
        return;
      }
      openPopup();
    });

    return { mode: 'popup', form: root, lead: lead, message: message, button: button, words: words, soldOut: soldOut };
  }

  /** `metaData` "contains all of the fields submitted to Klaviyo" (docs). */
  function popupFirstName(detail) {
    const meta = (detail && detail.metaData) || {};
    for (const key of KLAVIYO_NAME_KEYS) {
      const value = String(meta[key] || '').trim();
      if (value) return value;
    }
    return '';
  }

  /**
   * ONE listener, registered at load and never at mount — she can submit the
   * TIMED popup with the drawer shut, and the attribute still has to be written.
   * The next drawer paint then finds SUCCESS and shows the unlocked bar.
   */
  let popupListening = false;
  function onKlaviyoFormEvent(event) {
    const detail = (event && event.detail) || {};
    if (String(detail.type || '') !== KLAVIYO_SUBMIT_TYPE) return;
    const formId = String(detail.formId || detail.formID || '').trim();
    if (!POPUP_FORM_ID || formId !== POPUP_FORM_ID) return;
    const meta = detail.metaData || {};
    machine.claimFromPopup({ first_name: popupFirstName(detail), email: meta.email || '' }).then(afterSubmit);
  }

  /** Alia: one `alia:signup` per email/phone submit, on `document`. Matched by campaign. */
  function onAliaSignup(event) {
    const detail = (event && event.detail) || {};
    if (String(detail.campaignID == null ? '' : detail.campaignID).trim() !== POPUP_FORM_ID) return;
    machine.claimFromPopup({ first_name: '', email: detail.email || '' }).then(afterSubmit);
  }

  /** Studio: `olv:signup` per channel (email, then maybe sms). Matched by popup id; the first claims. */
  function onStudioSignup(event) {
    const detail = (event && event.detail) || {};
    if (String(detail.popupId == null ? '' : detail.popupId).trim() !== POPUP_FORM_ID) return;
    if (detail.step !== 'email' && detail.step !== 'sms') return;
    machine.claimFromPopup({ first_name: '', email: '' }).then(afterSubmit);
  }

  function listenForPopup() {
    if (popupListening || MODE !== 'popup') return;
    popupListening = true;
    if (POPUP_PROVIDER === 'alia') { document.addEventListener('alia:signup', onAliaSignup); return; }
    if (POPUP_PROVIDER === 'studio') { document.addEventListener('olv:signup', onStudioSignup); return; }
    window.addEventListener(KLAVIYO_FORM_EVENT, onKlaviyoFormEvent);
  }

  function afterSubmit(snapshot) {
    if (!snapshot || snapshot.state !== 'success') return snapshot;
    // LAW 2: we do not add the line. We ask cart.js for a pass and it decides.
    // `addFreeProduct` is cart.js's own public shim and only schedules a pass.
    readCart(true).then(() => {
      const drawer = document.querySelector('cart-drawer');
      if (window.cartFunctions && typeof window.cartFunctions.addFreeProduct === 'function') {
        window.cartFunctions.addFreeProduct(drawer);
      }
      if (drawer && drawer.dispatchEvent) {
        drawer.dispatchEvent(new CustomEvent('cart-drawer:update', { bubbles: true, detail: { signupGift: true } }));
      }
    });
    return snapshot;
  }

  /**
   * ONE SENTENCE IN THE BAR. The bar already owns a line of copy, and the
   * snippet has rendered the gated "One step left…" version of it. On success
   * that sentence is the thing that is now wrong, so the module rewrites it and
   * removes its own form rather than stacking a second sentence under the
   * first. The next server repaint re-renders the snippet's real earned or
   * progress copy over the top, which is the correct end state.
   *
   * The original markup is kept on the node so nothing is destroyed.
   */
  function barSentence(slot) {
    const bar = slot.closest ? slot.closest('.free-product-progress-bar-main') : null;
    return bar ? bar.querySelector('.free-product-progress-bar-successfull') : null;
  }

  function successLineFor(snapshot, words, soldOut) {
    const firstName = String(snapshot.first_name || words.firstName || '').trim();
    const unavailable = snapshot.state === 'gift_unavailable' || soldOut;
    const line = firstName
      ? (unavailable ? COPY.soldOutSuccess : COPY.success)
      : (unavailable ? COPY.soldOutSuccessNoName : COPY.successNoName);
    return text(line, { gift: words.gift, firstName: firstName });
  }

  function takeOverSentence(slot, snapshot, words, soldOut) {
    const node = barSentence(slot);
    if (!node) return false;
    if (node.dataset.signupGiftOriginal === undefined) node.dataset.signupGiftOriginal = node.innerHTML;
    node.textContent = successLineFor(snapshot, words, soldOut);
    node.classList.add('pgb-signup-said');
    return true;
  }

  function clearSlot(slot) {
    if (!slot) return;
    slot.replaceChildren();
    slot.hidden = true;
    if (mounted && mounted.slot === slot) mounted = null;
  }

  /**
   * 🔒 LOCKED GIFT ROWS — painted by assets/olivine-gift-brain.js (2026-10-21) from the ONE
   * gift state, for EVERY gift that is earned, gated and not yet claimed (rung or threshold).
   * This module no longer finds, shows or hides rows. It registers itself as the CLAIM
   * PROVIDER (`window.olvGiftClaim`, below): the brain renders rows only while a provider
   * exists and `canClaim()`, disables their button while `busy()`, and calls `unlock()` on a
   * tap. The rows never touch the cart: on signup the brain adds the $0.00 twin line.
   */
  function brainPaint() {
    const brain = window.OlivineGiftBrain;
    if (brain && typeof brain.paint === 'function') {
      try { brain.paint(); } catch (error) { /* the rows are cosmetic; the claim still works */ }
    }
  }

  /** True while at least one locked row is on screen (the brain owns them). */
  function lockedRowShowing() {
    const brain = window.OlivineGiftBrain;
    return !!(brain && typeof brain.lockedShowing === 'function' && brain.lockedShowing());
  }

  function syncLockedRows() {
    brainPaint();
  }

  function unlockFromRow() {
    const state = machine.getState().state;
    if (state === 'submitting') return;
    // Same branch as the claim button: a signup that already went through but
    // whose gift write failed is retried, never re-opened.
    if (state === 'identity_ok_gift_failed') {
      machine.retry().then(afterSubmit);
      return;
    }
    if (MODE === 'popup') { openPopup(); return; }
    // INLINE mode has no popup: take her to the form in the bar.
    const input = document.querySelector('.pgb-signup-slot:not([hidden]) input[name="email"]');
    if (input) {
      input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      input.focus({ preventScroll: true });
    }
  }

  /** POPUP MODE paint. No inputs exist, so there is no validation state here. */
  function renderPrompt(snapshot) {
    const words = mounted.words;
    const message = mounted.message;
    const state = snapshot.state;
    mounted.form.dataset.state = state;
    mounted.button.disabled = state === 'submitting';

    if (state === 'success' || state === 'gift_unavailable') {
      const slot = mounted.slot;
      const soldOut = mounted.soldOut;
      // In the drawer the bar's own sentence carries the line and the slot goes.
      if (takeOverSentence(slot, snapshot, words, soldOut)) {
        clearSlot(slot);
        return;
      }
      // On the product page there is no bar, so the lead line keeps it.
      mounted.form.classList.add('olv-signup-gift__form--done');
      if (mounted.lead) mounted.lead.textContent = successLineFor(snapshot, words, soldOut);
      mounted.button.hidden = true;
      message.hidden = true;
      return;
    }

    mounted.form.classList.remove('olv-signup-gift__form--done');
    mounted.button.hidden = false;
    if (state === 'submitting') {
      mounted.button.textContent = popupButtonLabel(words, mounted.soldOut);
      message.hidden = false;
      message.textContent = COPY.submitting;
      return;
    }
    if (state === 'identity_ok_gift_failed') {
      message.hidden = false;
      message.textContent = COPY.giftFailed;
      mounted.button.textContent = COPY.giftRetry;
      return;
    }
    mounted.button.textContent = popupButtonLabel(words, mounted.soldOut);
    message.hidden = true;
    message.textContent = '';
  }

  function render(snapshot) {
    syncLockedRows(snapshot);
    if (!mounted) return;
    if (mounted.mode === 'popup') { renderPrompt(snapshot); return; }
    const words = mounted.words;
    const message = mounted.message;
    const state = snapshot.state;
    mounted.form.dataset.state = state;
    mounted.firstInput.disabled = state === 'submitting';
    mounted.emailInput.disabled = state === 'submitting';
    mounted.button.disabled = state === 'submitting';

    if (state === 'success' || state === 'gift_unavailable') {
      const slot = mounted.slot;
      const soldOut = mounted.soldOut;
      // In the drawer the bar's own sentence carries the line and the form goes.
      if (takeOverSentence(slot, snapshot, words, soldOut)) {
        clearSlot(slot);
        return;
      }
      // On the product page there is no bar, so the collapsed form keeps it.
      mounted.form.classList.add('olv-signup-gift__form--done');
      mounted.fields.hidden = true;
      mounted.consent.hidden = true;
      mounted.body.hidden = true;
      mounted.heading.textContent = successLineFor(snapshot, words, soldOut);
      message.hidden = true;
      return;
    }

    mounted.form.classList.remove('olv-signup-gift__form--done');
    mounted.fields.hidden = false;
    mounted.consent.hidden = false;
    mounted.body.hidden = false;
    if (state !== 'failed_identity' && state !== 'identity_ok_gift_failed') {
      mounted.button.textContent = text(config.buttonLabel || (mounted.soldOut ? COPY.soldOutButton : COPY.button), words);
    }

    if (state === 'submitting') {
      message.hidden = false;
      message.textContent = COPY.submitting;
      return;
    }
    if (state === 'validation_errors') {
      message.hidden = false;
      message.textContent = snapshot.errors.first_name || snapshot.errors.email || '';
      return;
    }
    if (state === 'failed_identity') {
      message.hidden = false;
      message.textContent = COPY.identityFailed;
      mounted.button.textContent = COPY.identityRetry;
      return;
    }
    if (state === 'identity_ok_gift_failed') {
      message.hidden = false;
      message.textContent = COPY.giftFailed;
      mounted.button.textContent = COPY.giftRetry;
      return;
    }
    message.hidden = true;
    message.textContent = '';
  }

  function giftTitleFor(slot) {
    return String(slot.dataset.giftTitle || config.giftTitle || 'gift').trim() || 'gift';
  }

  function gateFor(slot) {
    const hero = String(slot.dataset.hero || '');
    const rules = document.querySelectorAll('.pgb-gift-rule[data-signup-gate]');
    for (const rule of rules) {
      if (String(rule.getAttribute('data-hero') || '') !== hero) continue;
      const gate = (rule.getAttribute('data-signup-gate') || '').trim();
      if (gate) return gate;
    }
    // The product page has no rule table until something is in the cart, so the
    // section's own metafield read is the fallback THERE and only there.
    if (slot.dataset.surface === 'product_page') return String(config.gate || '').trim();
    return '';
  }

  function unmount() {
    if (!mounted) return;
    const slot = mounted.slot;
    slot.hidden = true;
    slot.replaceChildren();
    mounted = null;
  }

  function mount(slot) {
    if (mounted && mounted.slot === slot && slot.contains(mounted.form)) return;
    const words = { gift: giftTitleFor(slot), firstName: '' };
    const built = MODE === 'popup' ? buildPrompt(slot, words) : buildForm(slot, words);
    built.slot = slot;
    slot.replaceChildren(built.form);
    slot.hidden = false;
    mounted = built;
    render(machine.getState());
  }

  function sync() {
    scheduled = false;
    if (!activeContext()) { unmount(); return; }
    syncLockedRows();
    const slots = Array.from(document.querySelectorAll('.pgb-signup-slot'));
    if (!slots.length) { mounted = null; return; }
    let snapshot = machine.getState();
    const arm = abArm();
    const slot = slots.find((candidate) => gateApplies(gateFor(candidate), arm));
    if (!slot) { unmount(); return; }

    // A RESTORED success is only trusted when the CART still carries the
    // attribute. Stale storage beside a cart that lost `_signup_gift` is
    // forgotten outright, so nothing downstream can render "You are in" over a
    // cart the reconciler will never give a gift to.
    if ((snapshot.state === 'success' || snapshot.state === 'gift_unavailable') && snapshot.restored && !signedUp()) {
      snapshot = machine.forget();
    }

    // DONE. Either the submit that just happened, or a reload landing on a bar
    // whose paint still shows the gated-unsigned sentence while the cart has
    // already been written.
    const done = snapshot.state === 'success' || snapshot.state === 'gift_unavailable';
    if (done) {
      const words = { gift: giftTitleFor(slot), firstName: snapshot.first_name || '' };
      if (takeOverSentence(slot, snapshot, words, config.giftAvailable === false)) {
        clearSlot(slot);
        return;
      }
      mount(slot);
      return;
    }

    // A cart that already carries the entitlement gets nothing.
    if (signedUp()) { unmount(); return; }
    // ONE ASK AT A TIME (Maycol 2026-10-19). While the locked row is on screen it
    // IS the ask, so the bar's claim button stays hidden. It comes back wherever no
    // row shows (one serum: "Claim my free Angled Blending Brush"), so that unlock
    // path is never lost.
    if (lockedRowShowing()) { unmount(); return; }
    mount(slot);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => readCart(false).then(sync).catch(() => { scheduled = false; }));
  }

  /**
   * EMAIL UNLOCK LINK — strip ONLY `olv_gift` from the address bar, keeping
   * every other pair byte for byte (utm_*, variant, _ab…) and the hash, so a
   * reload or a share of this URL does not carry the claim. Cosmetic: a
   * browser that refuses replaceState still gets its claim.
   */
  function stripGiftLinkParam() {
    try {
      const pairs = String(window.location.search || '').replace(/^\?/, '').split('&');
      const kept = pairs.filter((pair) => { const name = queryPairName(pair); return name !== GIFT_LINK_PARAM && name !== ADD_LINK_PARAM; });
      if (kept.length === pairs.length) return;
      const search = kept.join('&');
      window.history.replaceState(window.history.state, '', window.location.pathname + (search ? '?' + search : '') + window.location.hash);
    } catch (error) { /* the address bar is cosmetic */ }
  }

  /**
   * EMAIL UNLOCK LINK — the claim. Runs ONCE per module start, never from
   * pageshow, and strips the param BEFORE the first await, so a second copy of
   * this script finds nothing to claim. Then the SAME chain as a popup submit:
   * claimFromPopup → hold() → ONE /cart/update.js → afterSubmit → cart.js
   * decides the line (LAW 2). No /cart/add.js, no Klaviyo call, no PII: she
   * signed up on another browser, so there is no name or email here at all.
   */
  let giftLinkHandled = false;
  function claimFromGiftLink() {
    if (giftLinkHandled) return;
    giftLinkHandled = true;
    let present = false;
    let addQuantity = 0;
    let variantWanted = '';
    try {
      present = hasGiftLinkParam(window.location.search);
      addQuantity = addLinkQuantity(window.location.search);
      variantWanted = queryParam(window.location.search, 'variant');
    } catch (error) { present = false; addQuantity = 0; }
    stripGiftLinkParam();
    if (!present && !addQuantity) return;
    const claim = !present ? Promise.resolve() : readCart(false).then(() => {
      // A cart that already carries the entitlement is never written twice.
      if (signedUp()) return;
      // THE CART IS THE TRUTH: a success restored from storage beside a cart
      // without `_signup_gift` is stale (same rule as sync()), so it is
      // forgotten rather than allowed to make the claim a no-op.
      const snapshot = machine.getState();
      if (snapshot.state === 'success' && snapshot.restored) machine.forget();
      return machine.claimFromPopup({ first_name: '', email: '' }).then(afterSubmit);
    });
    if (addQuantity) claim.catch(() => {}).then(() => addHeroFromLink(addQuantity, variantWanted));
  }

  /**
   * ADD-TO-CART LINK — the add. A HERO line, never a gift line, so LAW 2
   * (cart.js alone adds gift lines) is untouched. Tops up, so a second click or
   * a reload adds nothing. Renders through the theme's own drawer
   * (`renderContents` opens it and fires cart-drawer:render, which runs the
   * cart.js gift pass). Any failure leaves the page exactly as it was.
   */
  async function addHeroFromLink(quantity, variantWanted) {
    try {
      const handle = String(config.heroHandle || '').trim();
      if (!handle) return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const productResponse = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
      if (!productResponse.ok) return;
      const product = await productResponse.json();
      const variant = pickLinkVariant(product, variantWanted);
      if (!variant) return;
      const missing = topUpQuantity(await cart.read(), product.id, quantity);
      // The drawer element must be upgraded before we ask it for sections.
      if (window.customElements && typeof window.customElements.whenDefined === 'function') {
        await Promise.race([window.customElements.whenDefined('cart-drawer'), new Promise((resolve) => setTimeout(resolve, 4000))]);
      }
      if (!missing) { openDrawer(); return; }
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id) : [];
      const addUrl = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : root + 'cart/add') + '.js';
      const response = await fetch(addUrl.replace(/\.js\.js$/, '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: missing }], sections: sections, sections_url: window.location.pathname })
      });
      if (!response.ok) return;
      const state = await response.json();
      if (drawer && typeof drawer.renderContents === 'function' && state && state.sections) {
        drawer.renderContents(state);
      } else {
        openDrawer();
      }
    } catch (error) { /* the page is untouched; she can still add by hand */ }
  }

  function openDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.refreshCartDrawer === 'function') { drawer.refreshCartDrawer(); return; }
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  machine.restore();
  listenForPopup();
  claimFromGiftLink();
  // 🧠 CLAIM PROVIDER for the gift brain's locked rows (see LOCKED GIFT ROWS above).
  window.olvGiftClaim = {
    canClaim: () => {
      const state = machine.getState().state;
      return state !== 'success' && state !== 'gift_unavailable' && !signedUp();
    },
    busy: () => machine.getState().state === 'submitting',
    unlock: unlockFromRow
  };
  document.dispatchEvent(new CustomEvent('olv-gift:claim-ready'));
  document.addEventListener('cart-drawer:update', schedule);
  document.addEventListener('cart-drawer:render', schedule);
  window.addEventListener('pageshow', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();
